import BarChart from "@/components/Common/BarChart/BarChart";
import LineChart from "@/components/Common/LineChart/LineChart";
import axios from "axios";
import moment from "moment";
import { getTokenHeader } from "@/helpers/commonHelper";
import { axis, barLabel, eur, num, paar, pct, tok } from "@/utils/format";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
const BASE = `${env.prompts_api_client_url}api/eo-prompt/interference`;
const LITELLM_PRICING_URL =
  "https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json";

const EUR_USD_RATE = 0.92;
const ALL_TENANTS_VALUE = "__all_tenants__";
const ALL_MODELS_VALUE = "__all_models__";
const DEFAULT_MODEL_VALUE = "claude-haiku-4-5-20251001";

const DEFAULT_HAIKU_PRICING = {
  modelKey: DEFAULT_MODEL_VALUE,
  inputCostPerTokenUsd: 0.0000008,
  outputCostPerTokenUsd: 0.000004,
  cacheCreationInputTokenCostUsd: 0.000001,
  cacheReadInputTokenCostUsd: 0.00000008
};

const ID_LIKE_REGEX = /^[A-Za-z0-9_:./-]{20,}$/;

const buildCostChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  layout: {
    padding: {
      top: 8,
      right: 4,
      left: 0,
      bottom: 0
    }
  },
  scales: {
    xAxes: [
      {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 5
        },
        gridLines: {
          display: false
        }
      }
    ],
    yAxes: [
      {
        display: false,
        gridLines: {
          display: false
        },
        ticks: {
          beginAtZero: true
        }
      }
    ]
  },
  tooltips: {
    callbacks: {
      label(tooltipItem, data) {
        const label = data?.labels?.[tooltipItem.index] || "";
        const value =
          data?.datasets?.[tooltipItem.datasetIndex]?.data?.[tooltipItem.index];
        return `${label} · ${eur(Number(value || 0))}`;
      }
    }
  },
  animation: {
    duration: 500,
    onComplete() {
      const chart = this.chart;
      const dataset = chart?.data?.datasets?.[0];
      const meta = chart?.getDatasetMeta(0);
      if (!dataset || !meta) return;

      const ctx = chart.ctx;
      ctx.save();
      ctx.fillStyle = "#121212";
      ctx.font = "700 11px Arial";
      ctx.textAlign = "center";

      meta.data.forEach((bar, index) => {
        const value = Number(dataset.data[index] || 0);
        if (!value) return;
        const text = `${barLabel(value)} €`;
        const x = bar?._model?.x;
        const y = bar?._model?.y;
        if (Number.isFinite(x) && Number.isFinite(y)) {
          ctx.fillText(text, x, y - 4);
        }
      });
      ctx.restore();
    }
  }
});

const buildLineChartOptions = ({ showLegend, valueFormatter }) => ({
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: showLegend },
  layout: {
    padding: {
      top: 6,
      right: 6,
      left: 0,
      bottom: 0
    }
  },
  scales: {
    xAxes: [
      {
        ticks: {
          autoSkip: true,
          maxTicksLimit: 5
        },
        gridLines: {
          display: false,
          drawBorder: false
        }
      }
    ],
    yAxes: [
      {
        gridLines: {
          display: false,
          drawBorder: false,
          drawTicks: false
        },
        ticks: {
          beginAtZero: true,
          maxTicksLimit: 3,
          callback: value => axis(value)
        }
      }
    ]
  },
  tooltips: {
    callbacks: {
      label(tooltipItem, data) {
        const datasetLabel =
          data?.datasets?.[tooltipItem.datasetIndex]?.label || "Wert";
        const value = Number(tooltipItem.value || 0);
        return `${datasetLabel}: ${valueFormatter(value)}`;
      }
    }
  }
});

export default {
  name: "CostManagement",
  components: {
    BarChart,
    LineChart
  },
  data() {
    return {
      loading: false,
      selectedRangeKey: "month",
      rangeOptions: [
        { text: "Heute", value: "today" },
        { text: "7 Tage", value: "7days" },
        { text: "Monat", value: "month" },
        { text: "12 Monate", value: "12months" }
      ],
      filters: {
        fromDate: "",
        toDate: "",
        principal_id: ALL_TENANTS_VALUE,
        model: ALL_MODELS_VALUE
      },
      filterOptions: {
        principals: []
      },
      modelOptions: [{ text: "Alle Modelle", value: ALL_MODELS_VALUE }],
      logs: [],
      pricing: {
        ...DEFAULT_HAIKU_PRICING,
        source: "fallback"
      },
      pricingEntries: [],
      pricingLoaded: false,
      pricingWarning: "",
      promptLabelByShortId: {},
      promptLabelByApiShortId: {},
      promptModelByShortId: {},
      promptModelByApiShortId: {},
      logsTruncated: false,
      summary: {
        totalRequests: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalCostEur: 0,
        activeTenants: 0,
        avgCostPerRequestEur: 0
      },
      breakdownView: "model",
      modelSortBy: "cost",
      modelSortDirection: "desc",
      expandedModels: {},
      modelBreakdown: [],
      promptBreakdown: [],
      topConsumers: [],
      costChart: { labels: [], datasets: [] },
      requestChart: { labels: [], datasets: [] },
      tokenChart: { labels: [], datasets: [] },
      costChartOptions: buildCostChartOptions(),
      requestChartOptions: buildLineChartOptions({
        showLegend: false,
        valueFormatter: value => num(value)
      }),
      tokenChartOptions: buildLineChartOptions({
        showLegend: true,
        valueFormatter: value => tok(value)
      }),
      chartRenderKey: 0,
      lastLoadedAt: null,
      filterDebounceMs: 250,
      filterDebounceTimer: null
    };
  },
  computed: {
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    },
    principals() {
      return this.$store.state.principals || [];
    },
    hasActiveDropdownFilters() {
      return (
        this.filters.principal_id !== ALL_TENANTS_VALUE ||
        this.filters.model !== ALL_MODELS_VALUE
      );
    },
    chartRangeHint() {
      if (this.selectedRangeKey === "12months") {
        return "Rollierende 12 Monate, pro Monat";
      }
      if (this.selectedRangeKey === "month") {
        return "Laufender Monat, pro Kalenderwoche";
      }
      if (this.selectedRangeKey === "7days") {
        return "Letzte 7 Tage, pro Tag";
      }
      return "Heute, pro Stunde";
    },
    tokenPairInOut() {
      return paar([
        this.summary.totalInputTokens,
        this.summary.totalOutputTokens
      ]);
    },
    tokenPairTotal() {
      return paar([
        this.summary.totalInputTokens + this.summary.totalOutputTokens,
        this.summary.totalInputTokens + this.summary.totalOutputTokens
      ]);
    },
    hasCostData() {
      return this.costChart.labels.length > 0;
    },
    hasRequestData() {
      return this.requestChart.labels.length > 0;
    },
    hasTokenData() {
      return this.tokenChart.labels.length > 0;
    },
    reportTimestampLabel() {
      const source = this.lastLoadedAt || new Date();
      return moment(source)
        .locale("de")
        .format("D. MMMM YYYY, HH:mm");
    },
    sortedModelBreakdown() {
      const list = [...this.modelBreakdown];
      const direction = this.modelSortDirection === "asc" ? 1 : -1;
      if (this.modelSortBy === "requests") {
        return list.sort((a, b) => (a.requests - b.requests) * direction);
      }
      if (this.modelSortBy === "model") {
        return list.sort(
          (a, b) => a.model.localeCompare(b.model, "en") * direction
        );
      }
      return list.sort((a, b) => (a.costEur - b.costEur) * direction);
    },
    sortedPromptBreakdown() {
      const list = [...this.promptBreakdown];
      const direction = this.modelSortDirection === "asc" ? 1 : -1;
      if (this.modelSortBy === "requests") {
        return list.sort((a, b) => (a.requests - b.requests) * direction);
      }
      if (this.modelSortBy === "model") {
        return list.sort(
          (a, b) => a.prompt.localeCompare(b.prompt, "de") * direction
        );
      }
      return list.sort((a, b) => (a.costEur - b.costEur) * direction);
    }
  },
  watch: {
    currentPrincipal() {
      this.loadAll();
    }
  },
  async created() {
    this.applyRangePreset(this.selectedRangeKey);
    await this.loadAll();
  },
  beforeDestroy() {
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        await this.ensurePrincipalsLoaded();
        await this.fetchPromptDisplaySources();
        await Promise.all([this.fetchPricing(), this.fetchFilters()]);
        await this.fetchLogsAndBuildView();
      } catch (error) {
        console.error("Failed to load LLM cost management data:", error);
      } finally {
        this.loading = false;
      }
    },
    async onRangeChange() {
      this.applyRangePreset(this.selectedRangeKey);
      await this.fetchLogsAndBuildView();
    },
    onPrincipalChange(value) {
      if (!value) {
        this.filters.principal_id = ALL_TENANTS_VALUE;
      }
      this.applyFilters();
    },
    onModelChange(value) {
      if (!value) {
        this.filters.model = ALL_MODELS_VALUE;
      }
      this.applyFilters();
    },
    selectModelFromBreakdown(model) {
      const selectedModel = String(model || "").trim();
      if (!selectedModel) return;
      this.filters.model = selectedModel;
      this.applyFilters();
    },
    resetFilters() {
      this.filters.principal_id = ALL_TENANTS_VALUE;
      this.filters.model = ALL_MODELS_VALUE;
      this.applyFilters();
    },
    applyFilters() {
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer);
      }
      this.filterDebounceTimer = setTimeout(async () => {
        this.filterDebounceTimer = null;
        await this.fetchLogsAndBuildView();
      }, this.filterDebounceMs);
    },
    applyRangePreset(rangeKey) {
      const today = new Date();
      const from = new Date(today);
      const to = new Date(today);

      if (rangeKey === "7days") {
        from.setDate(today.getDate() - 6);
      } else if (rangeKey === "month") {
        from.setDate(1);
      } else if (rangeKey === "12months") {
        from.setFullYear(today.getFullYear() - 1);
        from.setDate(today.getDate() + 1);
      }

      this.filters.fromDate = from.toISOString().split("T")[0];
      this.filters.toDate = to.toISOString().split("T")[0];
    },
    buildQueryParams(page, limit = 1000) {
      return {
        page,
        limit,
        compact: true,
        sortBy: "createdAt",
        sortOrder: "desc",
        fromDate: `${this.filters.fromDate}T00:00:00.000Z`,
        toDate: `${this.filters.toDate}T23:59:59.999Z`,
        principal_id:
          this.filters.principal_id &&
          this.filters.principal_id !== ALL_TENANTS_VALUE
            ? this.filters.principal_id
            : undefined,
        model:
          this.filters.model && this.filters.model !== ALL_MODELS_VALUE
            ? this.filters.model
            : undefined
      };
    },
    async fetchFilters() {
      const principalOptions = this.buildPrincipalOptions();
      const deduped = [];
      const seen = new Set();

      principalOptions.forEach(option => {
        const key = this.normalizeLookupKey(option.value);
        if (!key || seen.has(key)) return;
        seen.add(key);
        deduped.push(option);
      });

      this.filterOptions.principals = [
        { text: "Alle Mandanten", value: ALL_TENANTS_VALUE },
        ...deduped
      ];

      if (
        !this.filterOptions.principals.some(
          option => option.value === this.filters.principal_id
        )
      ) {
        this.filters.principal_id = ALL_TENANTS_VALUE;
      }
    },
    async fetchStats() {
      const headers = getTokenHeader();
      const params = this.buildQueryParams(1, 1);
      await axios.get(`${BASE}/stats`, { params, headers });
    },
    async fetchLogsAndBuildView() {
      this.loading = true;
      try {
        await this.fetchStats();

        const headers = getTokenHeader();
        const pageSize = 1000;
        const maxPages = 200;
        let page = 1;
        let totalPages = 1;
        const allItems = [];
        this.logsTruncated = false;

        while (page <= totalPages) {
          const params = this.buildQueryParams(page, pageSize);
          const res = await axios.get(`${BASE}/logs`, { params, headers });
          const items = res.data?.items || [];
          const pagination = res.data?.pagination || {};

          allItems.push(...items);
          totalPages = Number(pagination.pages || 1);
          page += 1;

          if (page > maxPages && page <= totalPages) {
            this.logsTruncated = true;
            break;
          }
        }

        this.updateModelOptionsFromLogs(allItems);
        this.logs = allItems;
        this.buildCostView(allItems);
        this.lastLoadedAt = new Date();
      } finally {
        this.loading = false;
      }
    },
    async fetchPricing() {
      if (this.pricingLoaded) return;

      this.pricingWarning = "";

      try {
        const res = await axios.get(LITELLM_PRICING_URL, { timeout: 8000 });
        const pricingMap = res.data || {};
        const hasPricingPayload = Object.keys(pricingMap).length > 0;

        if (!hasPricingPayload) {
          this.pricing = { ...DEFAULT_HAIKU_PRICING, source: "fallback" };
          this.pricingEntries = [];
          this.pricingWarning =
            "Preisdaten konnten nicht geladen werden (leere Antwort). Es wird mit Fallback-Preisen gerechnet.";
          return;
        }

        this.pricingEntries = this.buildPricingEntries(pricingMap);
        const resolved = this.resolveHaikuPricing(pricingMap);
        if (resolved) {
          this.pricing = { ...resolved, source: "litellm" };
        } else {
          this.pricing = { ...DEFAULT_HAIKU_PRICING, source: "fallback" };
          this.pricingWarning =
            "Preisdaten sind unvollständig. Es wird mit Fallback-Preisen gerechnet.";
        }
      } catch (error) {
        this.pricing = { ...DEFAULT_HAIKU_PRICING, source: "fallback" };
        this.pricingEntries = [];
        this.pricingWarning =
          "Preisdaten konnten nicht geladen werden. Es wird mit Fallback-Preisen gerechnet.";
      } finally {
        this.pricingLoaded = true;
      }
    },
    updateModelOptionsFromLogs(items = []) {
      const values = new Set();
      items.forEach(item => {
        const model = String(item?.model || "").trim();
        if (model) values.add(model);
      });

      const sortedModels = [...values].sort((a, b) => a.localeCompare(b, "en"));
      const options = [
        { text: "Alle Modelle", value: ALL_MODELS_VALUE },
        ...sortedModels.map(model => ({ text: model, value: model }))
      ];

      if (!options.some(option => option.value === DEFAULT_MODEL_VALUE)) {
        options.push({ text: DEFAULT_MODEL_VALUE, value: DEFAULT_MODEL_VALUE });
      }

      this.modelOptions = options;
      if (!options.some(option => option.value === this.filters.model)) {
        this.filters.model = ALL_MODELS_VALUE;
      }
    },
    buildPricingEntries(pricingMap) {
      return Object.entries(pricingMap || {})
        .map(([modelKey, modelConfig]) => {
          if (!modelConfig || typeof modelConfig !== "object") return null;

          const inputCostPerTokenUsd = this.extractCostPerToken(modelConfig, [
            "input_cost_per_token",
            "input_cost_per_token_priority",
            "input_cost_per_token_flex",
            "input_cost_per_token_batches"
          ]);
          const outputCostPerTokenUsd = this.extractCostPerToken(modelConfig, [
            "output_cost_per_token",
            "output_cost_per_token_priority",
            "output_cost_per_token_flex",
            "output_cost_per_token_batches"
          ]);
          if (!inputCostPerTokenUsd && !outputCostPerTokenUsd) return null;

          const cacheCreationInputTokenCostUsd = this.extractCostPerToken(
            modelConfig,
            [
              "cache_creation_input_token_cost",
              "cache_creation_input_token_cost_priority",
              "cache_creation_input_token_cost_flex"
            ]
          );
          const cacheReadInputTokenCostUsd = this.extractCostPerToken(
            modelConfig,
            [
              "cache_read_input_token_cost",
              "cache_read_input_token_cost_priority",
              "cache_read_input_token_cost_flex"
            ]
          );

          return {
            modelKey,
            modelKeyLookup: this.normalizeLookupKey(modelKey),
            pricing: {
              modelKey,
              inputCostPerTokenUsd,
              outputCostPerTokenUsd,
              cacheCreationInputTokenCostUsd:
                cacheCreationInputTokenCostUsd || inputCostPerTokenUsd,
              cacheReadInputTokenCostUsd:
                cacheReadInputTokenCostUsd || inputCostPerTokenUsd
            }
          };
        })
        .filter(Boolean);
    },
    resolveHaikuPricing(pricingMap) {
      const entries = Object.entries(pricingMap || {});
      if (!entries.length) return null;

      const preferredPatterns = [
        DEFAULT_MODEL_VALUE,
        "anthropic.claude-haiku-4-5-20251001-v1:0",
        "anthropic.claude-haiku-4-5@20251001"
      ];

      const exact = preferredPatterns
        .map(pattern =>
          entries.find(([key]) => key.toLowerCase() === pattern.toLowerCase())
        )
        .find(Boolean);

      const fuzzy =
        entries.find(([key]) => {
          const lower = key.toLowerCase();
          return lower.includes("haiku") && lower.includes("4-5");
        }) || entries.find(([key]) => key.toLowerCase().includes("haiku"));

      const target = exact || fuzzy;
      if (!target) return null;

      const [modelKey, modelConfig] = target;
      const inputCostPerTokenUsd = this.extractCostPerToken(modelConfig, [
        "input_cost_per_token",
        "input_cost_per_token_priority"
      ]);
      const outputCostPerTokenUsd = this.extractCostPerToken(modelConfig, [
        "output_cost_per_token",
        "output_cost_per_token_priority"
      ]);

      if (!inputCostPerTokenUsd || !outputCostPerTokenUsd) return null;

      return {
        modelKey,
        inputCostPerTokenUsd,
        outputCostPerTokenUsd,
        cacheCreationInputTokenCostUsd:
          this.extractCostPerToken(modelConfig, [
            "cache_creation_input_token_cost",
            "cache_creation_input_token_cost_priority"
          ]) || inputCostPerTokenUsd,
        cacheReadInputTokenCostUsd:
          this.extractCostPerToken(modelConfig, [
            "cache_read_input_token_cost",
            "cache_read_input_token_cost_priority"
          ]) || inputCostPerTokenUsd
      };
    },
    extractCostPerToken(modelConfig, perTokenKeys = []) {
      for (const key of perTokenKeys) {
        const perToken = Number(modelConfig?.[key]);
        if (Number.isFinite(perToken) && perToken > 0) {
          return perToken;
        }
      }

      if (perTokenKeys.includes("input_cost_per_token")) {
        const perMillion = Number(modelConfig?.input_cost_per_million);
        if (Number.isFinite(perMillion) && perMillion > 0) {
          return perMillion / 1000000;
        }
      }

      if (perTokenKeys.includes("output_cost_per_token")) {
        const perMillion = Number(modelConfig?.output_cost_per_million);
        if (Number.isFinite(perMillion) && perMillion > 0) {
          return perMillion / 1000000;
        }
      }

      return 0;
    },
    normalizeToken(value) {
      const normalized = Number(value || 0);
      return Number.isFinite(normalized) ? normalized : 0;
    },
    normalizeLookupKey(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },
    getPrincipalDisplayName(principal) {
      const name = String(principal?.name || principal?.title || "").trim();
      if (name) return name;
      return String(principal?.id || principal?._id || "").trim();
    },
    buildPrincipalOptions() {
      return [...this.principals]
        .map(principal => ({
          text: this.getPrincipalDisplayName(principal),
          value: principal?.id || principal?._id || ""
        }))
        .filter(option => this.normalizeLookupKey(option.value))
        .sort((a, b) => a.text.localeCompare(b.text, "de"));
    },
    async ensurePrincipalsLoaded() {
      if (Array.isArray(this.principals) && this.principals.length) return;
      await this.$store.dispatch("getPrincipals");
    },
    resolveTenantLabel(value) {
      const lookup = this.normalizeLookupKey(value);
      if (!lookup) return "";

      const principal = this.principals.find(item => {
        const idKey = this.normalizeLookupKey(item?.id || item?._id);
        const nameKey = this.normalizeLookupKey(item?.name || item?.title);
        return lookup === idKey || lookup === nameKey;
      });

      if (principal) {
        return this.getPrincipalDisplayName(principal);
      }

      return String(value || "").trim();
    },
    isIdLike(value) {
      const stringValue = String(value || "");
      return ID_LIKE_REGEX.test(stringValue) && !/\s/.test(stringValue);
    },
    formatTenantDisplayLabel(value) {
      const stringValue = String(value || "").trim();
      if (!stringValue) return "";
      if (this.isIdLike(stringValue)) {
        return `Unbekannter Mandant (ID ${stringValue.slice(0, 8)}...)`;
      }
      return stringValue;
    },
    resolvePrincipalIdFromTenant(tenantKey, tenantLabel = "") {
      const keyLookup = this.normalizeLookupKey(tenantKey);
      const labelLookup = this.normalizeLookupKey(tenantLabel);
      if (!keyLookup && !labelLookup) return "";

      const principal = this.principals.find(item => {
        const idKey = this.normalizeLookupKey(item?.id || item?._id);
        const nameKey = this.normalizeLookupKey(item?.name || item?.title);
        return (
          (keyLookup && (keyLookup === idKey || keyLookup === nameKey)) ||
          (labelLookup && (labelLookup === idKey || labelLookup === nameKey))
        );
      });

      return String(principal?.id || principal?._id || "").trim();
    },
    selectPrincipalFromConsumer(consumer) {
      const principalId = String(consumer?.principalId || "").trim();
      if (!principalId) return;
      this.filters.principal_id = principalId;
      this.applyFilters();
    },
    extractTenantKey(item) {
      const candidates = [item?.eo_apikey, item?.principal_id, item?.user];
      const found = candidates.find(candidate =>
        this.normalizeLookupKey(candidate)
      );
      return String(found || "").trim();
    },
    extractPromptKey(item) {
      const promptShortId = String(item?.promptShortId || "").trim();
      if (promptShortId) return this.formatPromptKey(promptShortId);

      const apiShortId = String(item?.apiShortId || "").trim();
      if (apiShortId) return this.formatPromptKey(apiShortId);

      return "Ohne Prompt";
    },
    isDeletedPromptLabel(value) {
      return String(value || "").startsWith("(gelöscht:");
    },
    async fetchPromptDisplaySources() {
      try {
        const headers = getTokenHeader();
        const principalId = this.currentPrincipal;
        const [principalPromptRes, sharedPromptRes] = await Promise.all([
          axios.get(`${env.prompts_api_client_url}api/eo-prompt/prompts`, {
            params: { principal_id: principalId },
            headers
          }),
          axios.get(`${env.prompts_api_client_url}api/eo-prompt/prompts`, {
            params: { principal_id: principalId, shared: true },
            headers
          })
        ]);

        const promptLabelByShortId = {};
        const promptLabelByApiShortId = {};
        const promptModelByShortId = {};
        const promptModelByApiShortId = {};
        const allPrompts = [
          ...(sharedPromptRes.data || []),
          ...(principalPromptRes.data || [])
        ];

        allPrompts.forEach(prompt => {
          const shortId = String(prompt?.shortId || "").trim();
          const apiShortId = String(prompt?.apiShortId || "").trim();
          const promptModel = this.extractPromptModel(prompt);
          const label = this.buildPromptLabel(
            prompt?.name,
            shortId || apiShortId
          );

          if (shortId) {
            promptLabelByShortId[this.normalizeLookupKey(shortId)] = label;
            if (promptModel) {
              promptModelByShortId[
                this.normalizeLookupKey(shortId)
              ] = promptModel;
            }
          }

          if (apiShortId) {
            promptLabelByApiShortId[
              this.normalizeLookupKey(apiShortId)
            ] = label;
            if (promptModel) {
              promptModelByApiShortId[
                this.normalizeLookupKey(apiShortId)
              ] = promptModel;
            }
          }
        });

        this.promptLabelByShortId = promptLabelByShortId;
        this.promptLabelByApiShortId = promptLabelByApiShortId;
        this.promptModelByShortId = promptModelByShortId;
        this.promptModelByApiShortId = promptModelByApiShortId;
      } catch (error) {
        this.promptLabelByShortId = {};
        this.promptLabelByApiShortId = {};
        this.promptModelByShortId = {};
        this.promptModelByApiShortId = {};
      }
    },
    extractPromptModel(prompt) {
      const candidates = [
        prompt?.model,
        prompt?.modelName,
        prompt?.llmModel,
        prompt?.providerModel
      ];
      const found = candidates.find(candidate =>
        this.normalizeLookupKey(candidate)
      );
      return String(found || "").trim();
    },
    buildPromptLabel(name, shortId) {
      const safeShortId = String(shortId || "").trim();
      const safeName = String(name || "").trim();
      if (!safeShortId) return "-";
      if (!safeName) return safeShortId;
      return safeName;
    },
    formatPromptKey(value) {
      if (!value) return "-";
      const normalized = this.normalizeLookupKey(value);
      const label =
        this.promptLabelByShortId[normalized] ||
        this.promptLabelByApiShortId[normalized];
      if (label) return label;
      return `(gelöscht: ${value})`;
    },
    isModelExpanded(model) {
      return !!this.expandedModels[model];
    },
    toggleModelExpanded(model) {
      this.$set(this.expandedModels, model, !this.expandedModels[model]);
    },
    setTableSort(sortBy) {
      if (!sortBy) return;
      if (this.modelSortBy === sortBy) {
        this.modelSortDirection =
          this.modelSortDirection === "asc" ? "desc" : "asc";
        return;
      }

      this.modelSortBy = sortBy;
      this.modelSortDirection = sortBy === "model" ? "asc" : "desc";
    },
    getTableSortIcon(sortBy) {
      if (this.modelSortBy !== sortBy) return "mdi-unfold-more-horizontal";
      return this.modelSortDirection === "asc"
        ? "mdi-arrow-up"
        : "mdi-arrow-down";
    },
    resolvePricingInfoForModel(modelValue) {
      const selectedModelFallback =
        this.filters.model && this.filters.model !== ALL_MODELS_VALUE
          ? this.filters.model
          : DEFAULT_MODEL_VALUE;

      const requestedModel = String(
        modelValue || selectedModelFallback || ""
      ).trim();
      const modelLookup = this.normalizeLookupKey(requestedModel);
      if (!modelLookup) {
        return {
          pricing: this.pricing,
          matchType: "global-fallback",
          resolvedModel: this.pricing?.modelKey || DEFAULT_MODEL_VALUE
        };
      }

      const exactMatch = this.pricingEntries.find(
        entry => entry.modelKeyLookup === modelLookup
      );
      if (exactMatch) {
        return {
          pricing: exactMatch.pricing,
          matchType: "exact",
          resolvedModel: exactMatch.pricing?.modelKey || requestedModel
        };
      }

      const includesMatch = this.pricingEntries.find(
        entry =>
          entry.modelKeyLookup.includes(modelLookup) ||
          modelLookup.includes(entry.modelKeyLookup)
      );
      if (includesMatch?.pricing) {
        return {
          pricing: includesMatch.pricing,
          matchType: "includes",
          resolvedModel: includesMatch.pricing?.modelKey || requestedModel
        };
      }

      const defaultLookup = this.normalizeLookupKey(DEFAULT_MODEL_VALUE);
      const defaultModelMatch = this.pricingEntries.find(
        entry =>
          entry.modelKeyLookup === defaultLookup ||
          entry.modelKeyLookup.includes(defaultLookup)
      );
      if (defaultModelMatch?.pricing) {
        return {
          pricing: defaultModelMatch.pricing,
          matchType: "default-model-fallback",
          resolvedModel:
            defaultModelMatch.pricing?.modelKey || DEFAULT_MODEL_VALUE
        };
      }

      return {
        pricing: this.pricing,
        matchType: "global-fallback",
        resolvedModel: this.pricing?.modelKey || DEFAULT_MODEL_VALUE
      };
    },
    resolvePricingForModel(modelValue) {
      return this.resolvePricingInfoForModel(modelValue).pricing;
    },
    resolveModelForLog(item) {
      const logModel = String(item?.model || "").trim();
      if (logModel) return logModel;

      const promptShortId = this.normalizeLookupKey(item?.promptShortId);
      if (promptShortId && this.promptModelByShortId[promptShortId]) {
        return this.promptModelByShortId[promptShortId];
      }

      const apiShortId = this.normalizeLookupKey(item?.apiShortId);
      if (apiShortId && this.promptModelByApiShortId[apiShortId]) {
        return this.promptModelByApiShortId[apiShortId];
      }

      if (this.filters.model && this.filters.model !== ALL_MODELS_VALUE) {
        return this.filters.model;
      }

      return DEFAULT_MODEL_VALUE;
    },
    calculateCostEur(item) {
      const resolvedModel = this.resolveModelForLog(item);
      const modelPricing = this.resolvePricingForModel(resolvedModel);
      const inputTokens = this.normalizeToken(item?.inputTokens);
      const outputTokens = this.normalizeToken(item?.outputTokens);
      const cacheCreationInputTokens = this.normalizeToken(
        item?.cacheCreationInputTokens
      );
      const cacheReadInputTokens = this.normalizeToken(
        item?.cacheReadInputTokens
      );

      const usd =
        inputTokens * modelPricing.inputCostPerTokenUsd +
        outputTokens * modelPricing.outputCostPerTokenUsd +
        cacheCreationInputTokens * modelPricing.cacheCreationInputTokenCostUsd +
        cacheReadInputTokens * modelPricing.cacheReadInputTokenCostUsd;

      return usd * EUR_USD_RATE;
    },
    getTimeScaleByRange() {
      if (this.selectedRangeKey === "today") return "hour";
      if (this.selectedRangeKey === "month") return "week";
      if (this.selectedRangeKey === "12months") return "month";
      return "day";
    },
    getBucketKey(dateValue, scale) {
      const value = moment(dateValue);
      if (!value.isValid()) return "";
      if (scale === "hour")
        return value.startOf("hour").format("YYYY-MM-DD HH:00");
      if (scale === "week")
        return value.startOf("isoWeek").format("YYYY-MM-DD");
      if (scale === "month") return value.startOf("month").format("YYYY-MM");
      return value.startOf("day").format("YYYY-MM-DD");
    },
    formatBucketLabel(bucketKey, scale) {
      if (!bucketKey) return "";
      if (scale === "hour")
        return moment(bucketKey, "YYYY-MM-DD HH:mm").format("HH:mm");
      if (scale === "month")
        return moment(bucketKey, "YYYY-MM")
          .locale("de")
          .format("MMMM YY");
      if (scale === "week") {
        const weekStart = moment(bucketKey, "YYYY-MM-DD");
        return `KW ${weekStart.isoWeek()}`;
      }
      return moment(bucketKey, "YYYY-MM-DD").format("DD.MM.");
    },
    buildCostView(items) {
      const bucketCostMap = {};
      const bucketRequestMap = {};
      const bucketInputTokenMap = {};
      const bucketOutputTokenMap = {};
      const tenantMap = {};
      const modelMap = {};
      const promptMap = {};
      const scale = this.getTimeScaleByRange();

      let totalInputTokens = 0;
      let totalOutputTokens = 0;
      let totalCostEur = 0;

      items.forEach(item => {
        const inputTokens = this.normalizeToken(item?.inputTokens);
        const outputTokens = this.normalizeToken(item?.outputTokens);
        const costEur = this.calculateCostEur(item);
        const modelName = this.resolveModelForLog(item);

        totalInputTokens += inputTokens;
        totalOutputTokens += outputTokens;
        totalCostEur += costEur;

        const bucketKey = this.getBucketKey(item.createdAt, scale);
        if (bucketKey) {
          bucketCostMap[bucketKey] = (bucketCostMap[bucketKey] || 0) + costEur;
          bucketRequestMap[bucketKey] = (bucketRequestMap[bucketKey] || 0) + 1;
          bucketInputTokenMap[bucketKey] =
            (bucketInputTokenMap[bucketKey] || 0) + inputTokens;
          bucketOutputTokenMap[bucketKey] =
            (bucketOutputTokenMap[bucketKey] || 0) + outputTokens;
        }

        if (!modelMap[modelName]) {
          modelMap[modelName] = {
            model: modelName,
            requests: 0,
            costEur: 0,
            promptMap: {}
          };
        }
        modelMap[modelName].requests += 1;
        modelMap[modelName].costEur += costEur;

        const promptKey = this.extractPromptKey(item);
        if (!promptMap[promptKey]) {
          promptMap[promptKey] = { prompt: promptKey, requests: 0, costEur: 0 };
        }
        promptMap[promptKey].requests += 1;
        promptMap[promptKey].costEur += costEur;

        if (!modelMap[modelName].promptMap[promptKey]) {
          modelMap[modelName].promptMap[promptKey] = {
            prompt: promptKey,
            requests: 0,
            costEur: 0
          };
        }
        modelMap[modelName].promptMap[promptKey].requests += 1;
        modelMap[modelName].promptMap[promptKey].costEur += costEur;

        const tenantKey = this.extractTenantKey(item);
        if (!tenantKey) return;

        const resolvedLabel = this.resolveTenantLabel(tenantKey) || tenantKey;
        if (!tenantMap[tenantKey]) {
          tenantMap[tenantKey] = {
            key: tenantKey,
            rawLabel: resolvedLabel,
            label: this.formatTenantDisplayLabel(resolvedLabel),
            isFallbackLabel: this.isIdLike(resolvedLabel),
            requests: 0,
            costEur: 0
          };
        }
        tenantMap[tenantKey].requests += 1;
        tenantMap[tenantKey].costEur += costEur;
      });

      const totalRequests = items.length;
      const maxConsumerCost = Math.max(
        ...Object.values(tenantMap).map(entry => entry.costEur),
        0
      );

      const topConsumers = Object.values(tenantMap)
        .sort((a, b) => b.costEur - a.costEur)
        .slice(0, 10)
        .map(entry => ({
          ...entry,
          principalId: this.resolvePrincipalIdFromTenant(
            entry.key,
            entry.rawLabel
          ),
          costShare:
            totalCostEur > 0
              ? Number(((entry.costEur / totalCostEur) * 100).toFixed(1))
              : 0,
          relativeWidth:
            maxConsumerCost > 0
              ? Math.max((entry.costEur / maxConsumerCost) * 100, 1.5)
              : 0
        }));

      const sortedBuckets = Object.keys(bucketCostMap).sort((a, b) =>
        a > b ? 1 : b > a ? -1 : 0
      );

      const labels = sortedBuckets.map(bucket =>
        this.formatBucketLabel(bucket, scale)
      );

      this.costChart = {
        labels,
        datasets: [
          {
            label: "Kosten",
            backgroundColor: "rgba(0, 99, 108, 0.22)",
            borderColor: "#00636C",
            borderWidth: 2,
            maxBarThickness: 54,
            data: sortedBuckets.map(bucket =>
              Number(bucketCostMap[bucket] || 0)
            )
          }
        ]
      };

      this.requestChart = {
        labels,
        datasets: [
          {
            label: "Anfragen",
            backgroundColor: "rgba(0, 99, 108, 0.16)",
            borderColor: "#00636C",
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.2,
            fill: true,
            data: sortedBuckets.map(bucket =>
              Number(bucketRequestMap[bucket] || 0)
            )
          }
        ]
      };

      this.tokenChart = {
        labels,
        datasets: [
          {
            label: "Eingabe",
            backgroundColor: "rgba(248, 176, 0, 0.18)",
            borderColor: "#F8B000",
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.2,
            fill: true,
            data: sortedBuckets.map(bucket =>
              Number(bucketInputTokenMap[bucket] || 0)
            )
          },
          {
            label: "Ausgabe",
            backgroundColor: "rgba(0, 99, 108, 0.16)",
            borderColor: "#00636C",
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
            tension: 0.2,
            fill: true,
            data: sortedBuckets.map(bucket =>
              Number(bucketOutputTokenMap[bucket] || 0)
            )
          }
        ]
      };

      this.chartRenderKey += 1;

      this.modelBreakdown = Object.values(modelMap)
        .sort((a, b) => b.costEur - a.costEur)
        .map(entry => ({
          ...entry,
          pricingInfo: this.resolvePricingInfoForModel(entry.model),
          requestShare:
            totalRequests > 0
              ? Number(((entry.requests / totalRequests) * 100).toFixed(1))
              : 0,
          costShare:
            totalCostEur > 0
              ? Number(((entry.costEur / totalCostEur) * 100).toFixed(1))
              : 0,
          promptBreakdown: Object.values(entry.promptMap)
            .sort((a, b) => b.costEur - a.costEur)
            .map(promptEntry => ({
              ...promptEntry,
              requestShare:
                entry.requests > 0
                  ? Number(
                      ((promptEntry.requests / entry.requests) * 100).toFixed(1)
                    )
                  : 0,
              costShare:
                entry.costEur > 0
                  ? Number(
                      ((promptEntry.costEur / entry.costEur) * 100).toFixed(1)
                    )
                  : 0
            }))
        }));

      this.promptBreakdown = Object.values(promptMap)
        .sort((a, b) => b.costEur - a.costEur)
        .map(entry => ({
          ...entry,
          requestShare:
            totalRequests > 0
              ? Number(((entry.requests / totalRequests) * 100).toFixed(1))
              : 0,
          costShare:
            totalCostEur > 0
              ? Number(((entry.costEur / totalCostEur) * 100).toFixed(1))
              : 0
        }));

      const nextExpandedModels = {};
      this.modelBreakdown.forEach(entry => {
        if (this.expandedModels[entry.model]) {
          nextExpandedModels[entry.model] = true;
        }
      });
      this.expandedModels = nextExpandedModels;

      this.topConsumers = topConsumers;
      this.summary = {
        totalRequests,
        totalInputTokens,
        totalOutputTokens,
        totalCostEur,
        activeTenants: Object.keys(tenantMap).length,
        avgCostPerRequestEur:
          totalRequests > 0 ? Number(totalCostEur / totalRequests) : 0
      };
    },
    formatInt(value) {
      return num(value);
    },
    formatTokens(value) {
      return tok(value);
    },
    formatCurrency(value) {
      return eur(Number(value || 0));
    },
    formatPercent(value) {
      return pct(Number(value || 0));
    },
    getPricingMatchLabel(matchType) {
      const mapping = {
        exact: "Preis gefunden",
        includes: "Ähnlicher Treffer",
        "default-model-fallback": "Fallback auf Default-Modell",
        "global-fallback": "Globaler Fallback"
      };
      return mapping[matchType] || "Fallback";
    },
    getPricingMatchClass(matchType) {
      const mapping = {
        exact: "pricing_match_badge--exact",
        includes: "pricing_match_badge--includes",
        "default-model-fallback": "pricing_match_badge--default",
        "global-fallback": "pricing_match_badge--fallback"
      };
      return mapping[matchType] || "pricing_match_badge--fallback";
    },
    formatUsdPerMillion(costPerTokenUsd) {
      const perMillion = Number(costPerTokenUsd || 0) * 1000000;
      return `${perMillion.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
      })} USD`;
    },
    getModelPricingTooltipLines(pricingInfo) {
      const pricing = pricingInfo?.pricing;
      if (!pricing) {
        return ["Keine Preisinformation verfügbar."];
      }

      const cacheUsesInputFallback =
        pricing.cacheCreationInputTokenCostUsd ===
          pricing.inputCostPerTokenUsd &&
        pricing.cacheReadInputTokenCostUsd === pricing.inputCostPerTokenUsd;

      const lines = [
        `Verwendetes Pricing-Modell: ${pricingInfo?.resolvedModel ||
          pricing.modelKey}`,
        `Input: ${this.formatUsdPerMillion(
          pricing.inputCostPerTokenUsd
        )} / 1 Mio Tokens`,
        `Output: ${this.formatUsdPerMillion(
          pricing.outputCostPerTokenUsd
        )} / 1 Mio Tokens`,
        `Cache Creation: ${this.formatUsdPerMillion(
          pricing.cacheCreationInputTokenCostUsd
        )} / 1 Mio Tokens`,
        `Cache Read: ${this.formatUsdPerMillion(
          pricing.cacheReadInputTokenCostUsd
        )} / 1 Mio Tokens`,
        `USD -> EUR Faktor: ${EUR_USD_RATE}`
      ];

      if (cacheUsesInputFallback) {
        lines.push(
          "Hinweis: Cache-Preise nutzen den Input-Preis als Fallback."
        );
      }

      return lines;
    }
  }
};
